import React, { useState, useRef, useEffect, useCallback } from 'react';

type DropdownPosition = 'above' | 'below';
type DropdownAlign = 'left' | 'right';

interface DropdownProps {
  /** Render the trigger element. Receives isOpen state and toggle function. */
  trigger: (props: { isOpen: boolean; toggle: () => void }) => React.ReactNode;
  /** Dropdown content (use DropdownItem components or custom content) */
  children: React.ReactNode;
  /** Open direction relative to trigger */
  position?: DropdownPosition;
  /** Horizontal alignment relative to trigger */
  align?: DropdownAlign;
  /** Min width of the dropdown panel */
  minWidth?: string;
  /** Max width of the dropdown panel */
  maxWidth?: string;
  /** Additional className for the dropdown panel */
  className?: string;
  /** Controlled open state (optional — uncontrolled by default) */
  isOpen?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (isOpen: boolean) => void;
  /** Close the dropdown when content is clicked (default: true) */
  closeOnSelect?: boolean;
}

export function Dropdown({
  trigger,
  children,
  position = 'above',
  align = 'left',
  minWidth,
  maxWidth,
  className = '',
  isOpen: controlledIsOpen,
  onOpenChange,
  closeOnSelect = true,
}: DropdownProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isOpen = controlledIsOpen ?? internalIsOpen;

  const setIsOpen = useCallback((value: boolean) => {
    if (controlledIsOpen === undefined) {
      setInternalIsOpen(value);
    }
    onOpenChange?.(value);
  }, [controlledIsOpen, onOpenChange]);

  const toggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, setIsOpen]);

  // Close on Escape key. The key is marked as handled, so a layer underneath
  // (full-screen video) does not act on the same press.
  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, setIsOpen]);

  const positionClasses = position === 'above'
    ? 'bottom-full mb-2'
    : 'top-full mt-2';

  const alignClasses = align === 'left' ? 'left-0' : 'right-0';

  const slideAnimation = position === 'above'
    ? 'animate-in slide-in-from-bottom-2'
    : 'animate-in slide-in-from-top-2';

  return (
    <div className="relative" ref={containerRef}>
      {trigger({ isOpen, toggle })}

      {isOpen && (
        <div
          className={`absolute w-full ${positionClasses} ${alignClasses} bg-game-dark border border-gray-700 p-1 z-[2002] rounded-lg shadow-xl ${slideAnimation} duration-200 ${className}`}
          style={{
            minWidth: minWidth || undefined,
            maxWidth: maxWidth || undefined,
          }}
          onClick={() => closeOnSelect && setIsOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}
